import Big from 'big.js';
import {IMAGES_V2} from '@/const/images';
import {PerpetualType} from '@/modules/markets/enum';

export const DefaultGroupName = 'Watchlist';

export const marketUtil = {
  combineDefaultSymbol(symbols, rawContractMarkets, rawSpotMarkets) {
    const items = [...rawContractMarkets, ...rawSpotMarkets]
      .filter(({quoteCurrency}) => quoteCurrency === 'USDT')
      .filter((market) => symbols.includes(market.symbol))
      .map(({baseCurrency, symbol, pairName}) => ({currency: baseCurrency, symbol, pairName, checked: true}));

    const markets = rawContractMarkets.filter(({quoteCurrency}) => quoteCurrency === 'USDT');
    const items2 = markets
      .filter((market) => !symbols.includes(market.symbol))
      .sort((a, b) => b.change24hEp - a.change24hEp)
      .slice(0, 3)
      .map(({baseCurrency, symbol, pairName}) => ({currency: baseCurrency, symbol, pairName, checked: true}));
    return [...items, ...items2];
  },
  compare(sorts, categoryId?) {
    const {key, sortKey, side} = sorts;
    const isAscending = side === 'Up';
    const KEY = key || sortKey;
    return function (a, b) {
      if (categoryId === 999999 && KEY === 'combinationTurnoverEv' && side === 'Normal') {
        return b.timeline[1] - a.timeline[1];
      }
      const value1 = a[KEY];
      const value2 = b[KEY];
      if (isNaN(value1) && isNaN(value2)) {
        return isAscending ? value1.localeCompare(value2) : value2.localeCompare(value1);
      }
      return isAscending ? value1 - value2 : value2 - value1;
    };
  },
  convertMarket(market) {
    const {type, contractSide, volume24hStr, turnoverStr, combinationTurnoverEv} = market;
    const volume = ['Perpetual', 'PerpetualV2'].includes(type) ? (contractSide > 0 ? turnoverStr : volume24hStr) : turnoverStr;
    return {...market, volume, combinationTurnoverEv};
  },
  filterCategoryId(items, market, categoryId) {
    if (!categoryId) {
      return true;
    }
    if (items.includes(categoryId) && market.quoteCurrency === categoryId) {
      return true;
    }
    return market.categoryIds.includes(categoryId);
  },
  filterCategoryIdForAdvvance(market, categoryId, contractType = null) {
    if (!categoryId) {
      return true;
    }
    if (categoryId === 999999) {
      return market.isNew;
    }
    if (contractType === PerpetualType.USDS || contractType === null) {
      return market.categoryIds.includes(categoryId);
    }
    return true;
  },
  filterCategoryIdForPilot(market, categoryId, contractType = null) {
    if (!categoryId) {
      return true;
    }
    if (categoryId === 999999) {
      return market.isNew;
    }
    if (contractType === PerpetualType.USDS || contractType === null) {
      return market.categoryIds.includes(categoryId);
    }
    return true;
  },
  filterWithChange24({changePercent}, {min, max}) {
    return changePercent >= min && changePercent <= max;
  },
  filterWithVolume({volume24h, volume}, {min, max}) {
    const vol = volume || volume24h;
    return vol >= min && vol <= max;
  },
  filterContract(market, contractType) {
    if (!contractType) return market;
    const {settleCurrency, quoteCurrency, categoryIds, perpProductSubType} = market;
    if (contractType === 'Inverse') {
      return settleCurrency !== quoteCurrency;
    }
    if (contractType === 'PositiveUsds') {
      return settleCurrency === quoteCurrency && quoteCurrency === 'USDT' && perpProductSubType === 'Normal';
    }
    if (contractType === 'PositiveUsdc') {
      return settleCurrency === quoteCurrency && quoteCurrency === 'USDC' && perpProductSubType === 'Normal';
    }
    if (contractType === 'Pilot') {
      return settleCurrency === quoteCurrency && quoteCurrency === 'USDT' && perpProductSubType === 'Pilot';
    }
    return categoryIds.includes(contractType);
  },
  genContractMarkets(markets, sorts, categoryId, searchKey) {
    const value = searchKey && searchKey.toUpperCase();
    return markets
      .filter((market) => this.filterContract(market, categoryId) && market.symbol.includes(value))
      .map(this.convertMarket)
      .sort(this.compare(sorts, categoryId));
  },
  // pilot contract专用
  genContractMarketsForPilot(markets, sorts, advancedFilter, categoryId, contractType, searchKey) {
    const {change, volume} = advancedFilter;
    const value = searchKey && searchKey.toUpperCase();
    return markets
      .filter(
        (market) =>
          (contractType === PerpetualType.Inverse ? market.contractSide < 0 : market.contractSide > 0) &&
          this.filterCategoryIdForPilot(market, categoryId, contractType) &&
          this.filterWithChange24(market, change) &&
          this.filterWithVolume(market, volume) &&
          market.pairName.includes(value)
      )
      .map(this.convertMarket)
      .sort(this.compare(sorts, categoryId));
  },
  genSpotMarkets(rawMarkets, sorts, categoryId, searchKey) {
    const isSpecialSort = categoryId === 'USDT' && sorts.sortKey === 'combinationTurnoverEv' && sorts.side === 'Normal';
    const items = categoryUtil.genQuotes(rawMarkets);
    const markets = this.convertSpotMarket(isSpecialSort, sorts, rawMarkets);
    const value = searchKey && searchKey.toUpperCase();
    const lastMarkets = markets.filter((market) => this.filterCategoryId(items, market, categoryId) && (market.symbol || market.coin).includes(value)).map(this.convertMarket);
    return isSpecialSort ? lastMarkets : lastMarkets.sort(this.compare(sorts, categoryId));
  },
  genSpotMarketsForAdvance(rawMarkets, sorts, advancedFilter, categoryId, quoteCurrency, searchKey) {
    const {change, volume} = advancedFilter;
    const isSpecialSort = quoteCurrency === 'USDT' && sorts.key === 'combinationTurnoverEv' && sorts.side === 'Normal';
    const value = searchKey && searchKey.toUpperCase();
    const markets = rawMarkets
      .filter((market) => {
        const {changePercent, combinationTurnoverEv} = market;
        return (
          market.quoteCurrency === quoteCurrency &&
          (market.symbol.includes(value) || market.pairName.replace(/\s/g, '').includes(value)) &&
          this.filterCategoryIdForAdvvance(market, categoryId) &&
          changePercent > change.min &&
          changePercent < change.max &&
          combinationTurnoverEv > volume.min &&
          combinationTurnoverEv < volume.max
        );
      })
      .map(this.convertMarket);
    const lastMarkets = this.convertSpotMarketForAdvance(isSpecialSort, quoteCurrency, sorts, markets);
    return isSpecialSort ? lastMarkets : lastMarkets.sort(this.compare(sorts, categoryId));
  },
  convertSpotMarket(isSpecialSort, sorts, markets) {
    if (isSpecialSort) {
      const token = 'sPTUSDT';
      const ptMarket = markets.find(({symbol}) => symbol === token);
      const excludePtMarkets = markets.filter(({symbol}) => symbol !== token);
      ptMarket && excludePtMarkets.sort((a, b) => b.turnoverEv - a.turnoverEv).splice(2, 0, ptMarket);
      return excludePtMarkets;
    }
    return markets;
  },
  convertSpotMarketForAdvance(isSpecialSort, fId, sorts, markets) {
    if (isSpecialSort) {
      const token = 'sPTUSDT';
      const ptMarket = markets.find(({symbol}) => symbol === token);
      const excludePtMarkets = markets.filter(({symbol}) => symbol !== token).sort((a, b) => b.combinationTurnoverEv - a.combinationTurnoverEv);
      ptMarket && excludePtMarkets.splice(2, 0, ptMarket);
      return excludePtMarkets;
    }
    return markets;
  },
  tooltip(content) {
    return {content, placement: 'bottom', style: {'max-width': '360px', padding: '26px 20px'}, background: '#fff', fontSize: '14px', color: '#777B83'};
  },
  /**
   * @typedef {'Init' | 'PreList' | 'Listed' | 'Suspend' | 'Delisted' | 'Hide'} TradePairStatus
   * @param {TradePairStatus} status
   * @returns
   */
  checkTradePairVisible(status) {
    return ['PreList', 'Listed', 'Suspend', 'Delisted'].includes(status);
  },
  sortWithListTime(markets) {
    return markets.filter(({status}) => status === 'Listed').sort((a, b) => b.listTime - a.listTime);
  },
};

export const categoryUtil = {
  /**
   * 整合分类，根据市场变化情况整合分类，按照平均涨幅由高到低排序，其中反向合约不适用该分类
   * @param {Array<T>} categories 原始分类
   * @param {Array<T>} markets 原始市场
   * @returns {Array<T>} 处理后的分类
   */
  createCategory(categories, markets) {
    if (!categories) return [];
    const allCategoryIds = [...new Set(markets.map((o) => o.categoryIds).flat())];
    return categories
      .filter(({id}) => allCategoryIds.includes(id))
      .map(({id, text}) => {
        const items = markets.filter(({categoryIds}) => categoryIds.includes(id));
        return items.reduce(
          (p, {changePercent}) => {
            p['changePercent'] = Big(changePercent).plus(p.changePercent);
            p['id'] = id;
            p['name'] = text;
            p['size'] = items.length;
            return p;
          },
          {changePercent: 0, id: 0, name: '-', icon: '', size: 0}
        );
      })
      .map(({changePercent, size, ...item}) => ({
        ...item,
        change: changePercent > 0 && size > 0 ? changePercent / size : 0,
      }))
      .sort((a, b) => b.change - a.change)
      .map(({change, ...item}, index) => {
        return index < 3 ? {...item, icon: IMAGES_V2.FIRE, isHot: true} : {...item, isHot: false};
      });
  },
  checkIsHotCategory(categoryIds, categories) {
    const isHotCategory = (categories || []).find(({id}) => categoryIds.includes(id));
    return isHotCategory?.isHot || false;
  },
  genQuotes(markets) {
    if (!markets) return [];
    const items = markets.filter(({status}) => status === 'Listed');
    const currencies = Array.from(new Set(items.map(({quoteCurrency}) => quoteCurrency)));
    return currencies.map((currency) => currency);
  },
};

export const watchlistUtil = {
  genSymbols(groupId, groups, contractMap, spotsMap, marginsMap) {
    const isDefault = this.isDefaultGroup(groupId, groups);

    const items = [];

    if (isDefault) {
      const watchlist = groups.find(({name}) => name === DefaultGroupName);
      const symbols = watchlist ? watchlist.symbols : [];
      const contractSymbols = this.sortGroupSymbol(symbols, 'CONTRACT');
      if (contractSymbols.length > 0) {
        const contractOptions = contractSymbols
          .map((symbol) => contractMap.get(symbol))
          .filter(Boolean)
          .map((o) => this.convertPair(o, 'Contract'));
        if (contractOptions.length > 0) {
          items.push({title: 'Contract', options: contractOptions});
        }
      }

      const spotSymbols = this.sortGroupSymbol(symbols, 'SPOT');
      if (spotSymbols.length > 0) {
        const spotOptions = spotSymbols
          .map((o) => spotsMap.get(o))
          .filter(Boolean)
          .map((o) => this.convertPair(o, 'Spot'));
        if (spotOptions.length > 0) {
          items.push({title: 'Spot', options: spotOptions});
        }

        const marginOptions = spotSymbols
          .map((o) => marginsMap.get(o))
          .filter(Boolean)
          .map((o) => this.convertPair(o, 'Margin'));

        if (marginOptions.length > 0) {
          items.push({title: 'Margin', options: marginOptions});
        }
      }
    } else {
      const watchlist = groups.find(({id}) => id === groupId);
      const symbols = watchlist ? watchlist.symbols : [];
      items.push({
        title: '',
        options: symbols
          .map(({symbol}) => contractMap.get(symbol) || spotsMap.get(symbol))
          .filter(Boolean)
          .map(({type, baseCurrency, quoteCurrency, leverage, symbol}) => ({type, baseCurrency, quoteCurrency, leverage, symbol})),
      });
    }
    return items;
  },
  isDefaultGroup(groupId, groups) {
    const group = groups.find(({id}) => id === groupId);
    return group?.name === DefaultGroupName;
  },
  sortGroupSymbol(symbols, type) {
    return symbols
      .filter(({symbolType}) => symbolType === type)
      .sort((a, b) => b.orderNum - a.orderNum)
      .map((o) => o.symbol);
  },
  convertPair(pair, module) {
    const {type, baseCurrency, quoteCurrency, leverage, symbol} = pair;
    return {type, baseCurrency, quoteCurrency, leverage: module === 'Margin' ? leverage : null, symbol};
  },
};
